// Service for managing Payment Integrity filter state in sessionStorage
// Single unified key 'piFilterState' stores all PI filter selections

import { PIFilterState } from '../types/piFilters.types';
import { getPIDefaultFilters } from '../constants/piFilterConfig';

/**
 * Single sessionStorage key for all PI filter state
 */
export const PI_FILTER_STATE_KEY = 'piFilterState';

/**
 * Get PI filters from the unified sessionStorage key
 */
// Migrate old LOB display-name values (stored before the code-based fix) to codes
const LOB_DISPLAY_NAME_TO_CODE: Record<string, string> = {
  'Commercial': 'EI',
  'Medicaid': 'CS',
  'Medicare': 'MR',
  'Individual Exchange': 'EXC',
  'individual exchange': 'EXC',
};

export const getPIFiltersFromSession = (): PIFilterState => {
  try {
    const defaults = getPIDefaultFilters();
    const stateStr = sessionStorage.getItem(PI_FILTER_STATE_KEY);
    if (!stateStr) return defaults;

    const stored = JSON.parse(stateStr);
    const rawLob = stored.lineOfBusiness || defaults.lineOfBusiness;
    const lineOfBusiness = LOB_DISPLAY_NAME_TO_CODE[rawLob] ?? rawLob;
    return {
      timePeriod: stored.timePeriod || defaults.timePeriod,
      taxId: stored.taxId || defaults.taxId,
      contractIds: Array.isArray(stored.contractIds) ? stored.contractIds : defaults.contractIds,
      lineOfBusiness,
      claimType: stored.claimType || defaults.claimType,
      serviceType: stored.serviceType || defaults.serviceType,
      vendor: stored.vendor || defaults.vendor,
    };
  } catch (error) {
    return getPIDefaultFilters();
  }
};

/**
 * Save PI filters to the unified sessionStorage key.
 * Also writes backward-compatible keys for external consumers
 * (piTimePeriod, reactState) that haven't migrated yet.
 */
export const savePIFiltersToSession = (filters: PIFilterState, changedKeys?: string[]): void => {
  try {
    // Write unified key
    sessionStorage.setItem(PI_FILTER_STATE_KEY, JSON.stringify({
      timePeriod: filters.timePeriod,
      taxId: filters.taxId,
      contractIds: filters.contractIds,
      lineOfBusiness: filters.lineOfBusiness,
      claimType: filters.claimType,
      serviceType: filters.serviceType,
      vendor: filters.vendor,
    }));

    // Backward-compat: piTimePeriod for external consumers (EvaluationCard, Table, etc.)
    if (filters.timePeriod) {
      sessionStorage.setItem('piTimePeriod', JSON.stringify({
        value: filters.timePeriod.value || '',
        name: filters.timePeriod.title || filters.timePeriod.value || '',
      }));
    }

    // Backward-compat: reactState for external consumers (commonUtilityServices, etc.)
    sessionStorage.setItem('reactState', JSON.stringify({
      claimType: filters.claimType,
      serviceType: filters.serviceType,
      vendor: filters.vendor,
    }));

    // Backward-compat: sync taxId to 'state' session key for CP (ReimbursementFilters-connect)
    // and journeyExtendedTinFilterSelected for Overview/other journeys
    // CP reads state.taxId as [{ Tin, Tinname }]
    try {
      const stateStr = sessionStorage.getItem('state');
      const state = stateStr ? JSON.parse(stateStr) : {};

      // Only sync taxId to state when it was explicitly changed by the caller.
      // When changedKeys is provided (partial update from updatePIFiltersInSession),
      // skip taxId sync unless 'taxId' is in the list. When changedKeys is not
      // provided (full state write from Redux), always sync.
      const shouldSyncTaxId = !changedKeys || changedKeys.includes('taxId');
      if (shouldSyncTaxId) {
        const mappedTaxIds = (filters.taxId || []).map((t: any) => ({ Tin: t.Tin, Tinname: t.TinName || t.Tin }));
        state.taxId = mappedTaxIds;

        // Sync journeyExtendedTinFilterSelected so Overview and other journeys pick up PI TIN selections
        if (!state.journeyExtendedTinFilterSelected) {
          state.journeyExtendedTinFilterSelected = {};
        }
        state.journeyExtendedTinFilterSelected.taxId = mappedTaxIds;
      }

      // Only sync claimsFilter when it was explicitly changed.
      const shouldSyncClaimType = !changedKeys || changedKeys.includes('claimType');
      if (shouldSyncClaimType) {
        const piClaim = (filters.claimType || 'All').toString();
        if (piClaim === 'Professional') {
          state.claimsFilter = 'PROFESSIONAL';
        } else if (piClaim === 'Facility') {
          state.claimsFilter = 'INSTITUTIONAL';
        } else {
          // Reset to 'All' — clear claimsFilter so other journeys (CP) see no filter
          delete state.claimsFilter;
        }
      }

      sessionStorage.setItem('state', JSON.stringify(state));
    } catch (e) {
      // ignore state sync errors
    }

    // Backward-compat: piServiceType for returnParamsValuesV2
    if (filters.serviceType && filters.serviceType !== 'All') {
      sessionStorage.setItem('piServiceType', JSON.stringify({ name: filters.serviceType }));
    } else {
      sessionStorage.removeItem('piServiceType');
    }

    // Backward-compat: piVendors for returnParamsValuesV2
    if (filters.vendor && filters.vendor !== 'All') {
      sessionStorage.setItem('piVendors', JSON.stringify([{ name: filters.vendor }]));
    } else {
      sessionStorage.removeItem('piVendors');
    }
  } catch (error) {
    // silent
  }
};

/**
 * Update specific fields in the unified PI filter state without overwriting others
 */
export const updatePIFiltersInSession = (partial: Partial<PIFilterState>): void => {
  try {
    const current = getPIFiltersFromSession();
    const updated = { ...current, ...partial };
    savePIFiltersToSession(updated, Object.keys(partial));
  } catch (error) {
    // silent
  }
};



/**
 * Clear all PI filter state from sessionStorage
 */
export const clearPIFiltersFromSession = (): void => {
  try {
    sessionStorage.removeItem(PI_FILTER_STATE_KEY);
    sessionStorage.removeItem('piTimePeriod');
    sessionStorage.removeItem('reactState');
    sessionStorage.removeItem('piServiceType');
    sessionStorage.removeItem('piVendors');
  } catch (error) {
    // silent
  }
};

